import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.debug('Logout error:', e);
    }
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      const legacyToken = localStorage.getItem('token');
      const config = legacyToken ? { headers: { Authorization: `Bearer ${legacyToken}` } } : {};
      try {
        const response = await axios.get(`${API}/auth/me`, config);
        setUser(response.data);
        if (legacyToken) {
          try {
            await axios.post(`${API}/auth/refresh-cookie`, {}, config);
            localStorage.removeItem('token');
          } catch (e) {
            console.debug('Migrazione cookie fallita:', e);
          }
        }
      } catch (error) {
        if (legacyToken) localStorage.removeItem('token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  }, []);

  const value = useMemo(() => ({
    user, loading, login, logout,
    isAuthenticated: !!user,
    isAdmin: () => user?.ruolo === 'admin',
    isTecnico: () => user?.ruolo === 'tecnico',
    isOperatore: () => user?.ruolo === 'operatore',
    isCommerciale: () => user?.ruolo === 'commerciale',
  }), [user, loading, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
